import { forwardRef, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StatusBannerAutoHide } from '@xing-com/banner';
import type { ButtonProps, ButtonVariants } from '@xing-com/button';
import type { JobBookmarkState } from '@xing-com/crate-common-graphql-types';
import { useLocation } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import {
  SEARCH_ALERTS_EMAIL_BANNER_STATE,
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_ALERTS_MODAL_TYPES,
  SEARCH_STATE_PARAMS,
} from '@xing-com/crate-jobs-constants';
import type { JobDetails } from '@xing-com/crate-jobs-helpers';
import { removeBracketedText } from '@xing-com/crate-jobs-helpers';
import {
  useSearchAlerts,
  useCreateSearchAlertByQuery,
  useDeleteSearchAlert,
  useShowSearchAlertErrorBanner,
} from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import {
  IconSearchAlertActivated,
  IconNotificationCenter,
} from '@xing-com/icons';

import {
  StyledTipBox,
  StyledTipBoxContainer,
  StyledButton,
} from './alert-button.styles';

type Props = {
  bookmarkState?: JobBookmarkState;
  job: JobDetails;
  iconOnly?: boolean;
  size?: 'small' | 'medium';
  sizeConfined?: 'small' | 'medium';
  variant?: ButtonVariants;
};

type TipBoxTriggerButtonProps = Omit<ButtonProps, 'innerRef'> & {
  $isWide: boolean;
};

// Radix needs a real trigger ref; XDS Button exposes it through innerRef.
const TipBoxTriggerButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  TipBoxTriggerButtonProps
>((props, ref) => <StyledButton {...props} innerRef={ref} />);

TipBoxTriggerButton.displayName = 'TipBoxTriggerButton';

export const AlertButton: React.FC<Props> = ({
  job,
  iconOnly = false,
  size = 'medium',
  sizeConfined,
  variant = 'secondary',
}) => {
  const { formatMessage } = useIntl();
  const trackNwt = useJobsNwtTracking();
  const { isLoggedIn } = useLoginState();
  const { setSearchParameters } = useSearchParameter();
  const { data, loading } = useSearchAlerts();
  const nwtPage = job.redirectsToThirdPartyUrl
    ? 'jobs/detail_external'
    : 'jobs/detail';
  const [createSearchAlertLI, { isLoading: createAlertLoading }] =
    useCreateSearchAlertByQuery({
      nwtPage,
    });
  const onDeleteError = useShowSearchAlertErrorBanner('DELETE');
  const [deleteSearchAlert, { isLoading: deleteAlertLoading }] =
    useDeleteSearchAlert({
      onError: () => {
        onDeleteError();
      },
    });
  const { state } = useLocation();
  const [alertId, setAlertId] = useState('');
  const [globalId, setGlobalId] = useState('');
  const [showTipBox, setShowTipBox] = useState(false);
  const [loAlertCreated, setLoAlertCreated] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const jobLocations =
    job.locations.length > 0
      ? job.locations.map((location) => location.city).join(', ')
      : job.location.city;
  const keywords = `${removeBracketedText(job.title)} in ${jobLocations}`;
  const getMatchingAlertForKeywords = (): {
    matchingAlertId?: string;
    matchingGlobalId?: string;
  } => {
    if (
      loading ||
      data?.viewer?.searchAlerts?.__typename !== 'SearchAlertResultResponse'
    ) {
      return {};
    }

    const matchingAlert = data.viewer.searchAlerts.collection?.find(
      (alert) => alert?.query?.body?.keywords === keywords
    );

    return {
      matchingAlertId: matchingAlert?.id,
      matchingGlobalId: matchingAlert?.globalId,
    };
  };
  const { matchingAlertId, matchingGlobalId } = getMatchingAlertForKeywords();
  const [tipBox, setTipBox] = useLocalStorage('jdp_tip-box', {
    counter: 0,
  });

  const handleOnAlertClick = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoggedIn) {
      if (alertId) {
        deleteSearchAlert({
          alertId,
          nwtTrackingOptions: {
            page: nwtPage,
            itemUrn: globalId,
          },
          onCompleted: () => {
            setAlertId('');
            setGlobalId('');
            setShowSuccessBanner(true);
          },
        });
      } else {
        createSearchAlertLI({
          variables: {
            keywords,
          },
          onCompleted: (response) => {
            if (
              response.createSearchAlertByQueryNew?.__typename ===
              'SearchAlertCreationSuccess'
            ) {
              setAlertId(response.createSearchAlertByQueryNew.alert.id);
              setGlobalId(response.createSearchAlertByQueryNew.alert.globalId);
              setShowSuccessBanner(true);
              trackNwt({
                event: 'activated',
                eventSchema: 'basic',
                page: nwtPage,
                element: 'search_alert',
                itemUrn: response.createSearchAlertByQueryNew.alert.globalId,
              });
            }
          },
        });
      }
    } else {
      setSearchParameters({
        [SEARCH_STATE_PARAMS.keywords]: keywords,
        [SEARCH_ALERTS_MODAL_PARAM]: SEARCH_ALERTS_MODAL_TYPES.JOB_DETAILS,
      });
    }
  };

  useEffect(() => {
    if (
      state &&
      typeof state === 'object' &&
      SEARCH_ALERTS_EMAIL_BANNER_STATE in state
    ) {
      setLoAlertCreated(true);
    }
  }, [state]);

  useEffect(() => {
    if (!alertId && matchingAlertId && !globalId && matchingGlobalId) {
      setAlertId(matchingAlertId);
      setGlobalId(matchingGlobalId);
    }
    // don't add alertId
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [matchingAlertId, matchingAlertId]);

  useEffect(() => {
    setTipBox({
      counter: tipBox.counter + 1,
    });
    setShowTipBox(tipBox.counter + 1 <= 2);
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, []);

  const alertCreated = loAlertCreated || !!alertId;
  const buttonCopyId = alertCreated
    ? 'JOBS_SEARCH_ALERT_CREATED'
    : 'JOBS_CREATE_SEARCH_ALERT';
  const tipBoxHeadline = formatMessage({
    id: 'JOBS_DETAIL_SEARCH_ALERT_TIPBOX_HEADLINE',
  });
  const bannerCopyIds = alertCreated
    ? ['JOB_JOB_DETAILS_ALERT_CREATED_1', 'JOB_JOB_DETAILS_ALERT_CREATED_2']
    : ['JOB_JOB_DETAILS_ALERT_REMOVED_1', 'JOB_JOB_DETAILS_ALERT_REMOVED_2'];

  return (
    <>
      <StyledTipBoxContainer $isWide={!iconOnly}>
        <StyledTipBox
          $isWide={!iconOnly}
          align="center"
          bodyText={formatMessage({
            id: 'JOBS_DETAIL_SEARCH_ALERT_TIPBOX_DESCRIPTION',
          })}
          headlineText={tipBoxHeadline}
          markerText="New"
          aria-label={tipBoxHeadline}
          showArrow
          showClosingIndicator
          side={iconOnly ? 'top' : 'right'}
          variant="highlighted"
          open={showTipBox && isLoggedIn}
          onOpenChange={(open) => {
            setShowTipBox(open);
          }}
          triggerProps={{ asChild: true }}
          trigger={
            <TipBoxTriggerButton
              size={size}
              sizeConfined={sizeConfined}
              variant={variant}
              loading={loading || createAlertLoading || deleteAlertLoading}
              data-testid={
                alertCreated
                  ? 'job-details-delete-alert-button'
                  : 'job-details-add-alert-button'
              }
              icon={
                alertCreated ? IconSearchAlertActivated : IconNotificationCenter
              }
              onClick={handleOnAlertClick}
              $isWide={!iconOnly}
              aria-label={formatMessage({ id: buttonCopyId })}
            >
              {!iconOnly ? <FormattedMessage id={buttonCopyId} /> : null}
            </TipBoxTriggerButton>
          }
        />
      </StyledTipBoxContainer>

      <StatusBannerAutoHide
        variant="success"
        display="fixed"
        show={showSuccessBanner}
        showCloseButton
        handleOnClose={() => {
          setShowSuccessBanner(false);
        }}
      >
        <b>
          <FormattedMessage id={bannerCopyIds[0]} />
        </b>{' '}
        <FormattedMessage id={bannerCopyIds[1]} />
      </StatusBannerAutoHide>
    </>
  );
};
